import {Strings} from '@/assets';
import lengths from './lengths';

class Utility {
  assign = <T extends object, U extends object>(target: T, source = {}) => {
    if (!source) return;
    Object.assign(
      target,
      new Proxy(source, {
        get(_, p) {
          const sourceValue = Reflect.getOwnPropertyDescriptor(source, p);
          const targetValue = Reflect.getOwnPropertyDescriptor(target, p);
          if (targetValue && targetValue.value !== undefined) {
            if (Array.isArray(targetValue.value)) {
              const sourceArray = sourceValue?.value as U[];
              if (Array.isArray(sourceArray)) {
                return sourceArray;
              } else {
                return targetValue.value;
              }
            } else if (typeof targetValue.value === typeof sourceValue?.value) {
              return sourceValue?.value;
            } else {
              return sourceValue?.value;
            }
          } else {
            return undefined;
          }
        },
      }),
    );
  };

  static parseMrz(mrz: MrzResponse) {
    return Object.keys(mrz).reduce((acc, key) => {
      const value = mrz[key as keyof typeof mrz];
      if (value) {
        return {...acc, [key]: value};
      } else {
        return acc;
      }
    }, {});
  }

  static #BASE_URL = '';

  static get isProd() {
    return import.meta.env.PROD && !import.meta.env.VITE_BASE_URL;
  }

  static get BASE_URL() {
    if (import.meta.env.VITE_BASE_URL) {
      return import.meta.env.VITE_BASE_URL;
    } else if (import.meta.env.DEV) {
      return 'https://stg.duverify.com';
    } else {
      return this.#BASE_URL;
    }
  }

  static set BASE_URL(subdomain) {
    this.#BASE_URL = `https://${subdomain}.duverify.com`;
  }

  static get = (obj: object, path: string, defaultValue = null) => {
    const keys = Array.isArray(path)
      ? path
      : path.split('.').filter(key => key.length > 0);
    return keys.reduce((acc, key) => {
      return acc && acc[key] !== 'undefined' ? acc[key] : defaultValue;
    }, obj);
  };

  static get API_URL() {
    return `${this.BASE_URL}`;
  }

  static isSizeValid(image: File, save: (image: File) => void) {
    const maxSizeInBytes = 1000000;
    if (image.size <= maxSizeInBytes) {
      save(image);
    } else {
      alert('image size should be less than or equal to 1MB');
    }
  }

  static hideLoader() {
    const loader = document.querySelector('.loader_main_wrapper');
    if (loader) {
      loader.remove();
    }
  }

  static showLoader(loaderText?: string) {
    const mainWrapper = document.createElement('div');
    mainWrapper.classList.add('loader_main_wrapper');
    const loader = document.createElement('div');
    loader.classList.add('main-loader');
    loader.appendChild(document.createElement('div'));
    loader.appendChild(document.createElement('div'));
    mainWrapper.appendChild(loader);
    document.body.appendChild(mainWrapper);
    if (loaderText) {
      const div = document.createElement('div');
      div.innerHTML = loaderText;
      div.setAttribute(
        'style',
        'color:white; margin-top:10px; padding:0px 47px; text-align:center',
      );
      mainWrapper.appendChild(div);
    }
  }

  static validatePassport = (mrz: MrzResponse) => {
    let error = null;
    if (!mrz?.valid) {
      error = Strings.INVALID_PASSPORT;
    } else if (mrz.document_code !== 'P') {
      error = Strings.NOT_A_PASSPORT;
    } else if (Date.parse(mrz.expiration_date) < new Date().getTime()) {
      error = Strings.EXPIRED_PASSPORT;
    }
    return error;
  };

  static validateNewPassport = (response: NewVisaRequestResponse) => {
    const mrz = response.mrz_response;
    const error = Utility.validatePassport(mrz);
    let url = null;
    let update_url = null;
    if (!error) {
      url = `visa_requests/${response.visa_request_id}/visitor/${response.visitor_id}/${Strings.ROUTES.PASSPORT}`;
      update_url = `/api/visa_requests/${response.visa_request_id}/visitors/${response.visitor_id}/`;
    }
    return {error, url, update_url, mrz: Utility.parseMrz(mrz)};
  };

  static isPhoneValid = (arg: {phone: string; code: string}) => {
    try {
      const length = lengths[arg.code as unknown as keyof typeof lengths];
      if (length === undefined) return true;
      if (Array.isArray(length)) {
        return length.includes(arg.phone.length);
      } else if (arg.phone.length === length) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  static getRouteWithCurrentParams(route: string) {
    const params = new URL(window.location.href).searchParams;
    let newRoute = route;
    if (params.size > 0) {
      newRoute += `?${params.toString()}`;
    }
    return newRoute;
  }

  static clearInternal(id: string) {
    const key = btoa(`internal_${id}`);
    localStorage.removeItem(key);
  }

  static isInternal() {
    const params = new URL(window.location.href).searchParams;
    return params.has('internal');
  }
}

export default Utility;
